import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ─── Global 401 handling (no more "soft logout" blank-data state) ──────────────
// A single handler, registered by App.jsx, is invoked whenever ANY backend call
// (axios OR fetch) returns 401. App.jsx first tries one silent token refresh and,
// only if that fails, forces a hard logout so the user re-authenticates instead of
// staring at an unresponsive app.
let _onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  _onUnauthorized = fn;
}
function _notifyUnauthorized() {
  try { _onUnauthorized && _onUnauthorized(); } catch (e) { /* ignore */ }
}

// Axios: fire the handler on any 401 response (covers every axios-based call).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) _notifyUnauthorized();
    return Promise.reject(error);
  }
);

// ─── Active project (Campaign Projects) ───────────────────────────────────────
// The active project id is kept here (set by ProjectContext) and attached as the
// 'X-Project-Id' header to every backend call from ONE place, so individual API
// functions and pages don't each have to thread it through. Absent → the backend
// resolves the Default Project, so this is safe before the UI sets anything.
let _activeProjectId = null;
export function setActiveProjectId(id) {
  _activeProjectId = id || null;
}
export function getActiveProjectId() {
  return _activeProjectId;
}
function _isApiUrl(url) {
  return typeof url === "string" && API_BASE && url.startsWith(API_BASE);
}

// Axios: attach X-Project-Id on requests to our API.
axios.interceptors.request.use((config) => {
  if (_activeProjectId) {
    const full = (config.baseURL || "") + (config.url || "");
    if (_isApiUrl(config.url) || _isApiUrl(full) || !/^https?:\/\//.test(config.url || "")) {
      config.headers = config.headers || {};
      config.headers["X-Project-Id"] = _activeProjectId;
    }
  }
  return config;
});

// Fetch: wrap window.fetch once so fetch-based calls (pages that use fetch) also
// (a) carry X-Project-Id on requests to our API and (b) trigger the 401 handler.
// The original response is returned unchanged.
if (typeof window !== "undefined" && !window.__boomerangFetchPatched) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    try {
      const url = typeof input === "string" ? input : (input && input.url);
      if (_activeProjectId && _isApiUrl(url)) {
        const headers = new Headers((init && init.headers) || (typeof input !== "string" && input.headers) || {});
        if (!headers.has("X-Project-Id")) headers.set("X-Project-Id", _activeProjectId);
        init = { ...init, headers };
      }
    } catch (e) { /* ignore — never break a request over header injection */ }
    const res = await _origFetch(input, init);
    try { if (res && res.status === 401) _notifyUnauthorized(); } catch (e) { /* ignore */ }
    return res;
  };
  window.__boomerangFetchPatched = true;
}

// ─── Company / Project registry API (task 11.2) ───────────────────────────────
export async function listCompanies() {
  const headers = await getAuthHeaders();
  const res = await axios.get(`${API_BASE}/companies`, { headers });
  return res.data; // { companies: [...] }
}
export async function createCompany(displayName) {
  const headers = await getAuthHeaders();
  const res = await axios.post(`${API_BASE}/companies`, { display_name: displayName }, { headers });
  return res.data;
}
export async function renameCompany(companyId, displayName) {
  const headers = await getAuthHeaders();
  const res = await axios.patch(`${API_BASE}/companies/${encodeURIComponent(companyId)}`, { display_name: displayName }, { headers });
  return res.data;
}
export async function listProjects(status = "active") {
  const headers = await getAuthHeaders();
  const res = await axios.get(`${API_BASE}/projects`, { headers, params: { status } });
  return res.data; // { projects: [...] }
}
export async function createProject(displayName, companyName = null) {
  const headers = await getAuthHeaders();
  const body = { display_name: displayName };
  if (companyName) body.company_name = companyName;
  const res = await axios.post(`${API_BASE}/projects`, body, { headers });
  return res.data;
}
export async function renameProject(projectId, { displayName, companyId } = {}) {
  const headers = await getAuthHeaders();
  const body = {};
  if (displayName != null) body.display_name = displayName;
  if (companyId != null) body.company_id = companyId;
  const res = await axios.patch(`${API_BASE}/projects/${encodeURIComponent(projectId)}`, body, { headers });
  return res.data;
}
export async function archiveProject(projectId) {
  const headers = await getAuthHeaders();
  const res = await axios.post(`${API_BASE}/projects/${encodeURIComponent(projectId)}/archive`, {}, { headers });
  return res.data;
}
export async function reactivateProject(projectId) {
  const headers = await getAuthHeaders();
  const res = await axios.post(`${API_BASE}/projects/${encodeURIComponent(projectId)}/reactivate`, {}, { headers });
  return res.data;
}
export async function deleteProject(projectId) {
  const headers = await getAuthHeaders();
  const res = await axios.delete(`${API_BASE}/projects/${encodeURIComponent(projectId)}`, { headers });
  return res.data;
}

async function getAuthHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Get a fresh ID token (auto-refreshes if expired).
 * Use this instead of the stale idToken prop for long-lived pages.
 */
export async function getFreshToken() {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

/**
 * Compute SHA-256 hash of a File object (browser native).
 */
export async function computeFileHash(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Request a pre-signed S3 URL from the backend.
 */
// `projectId` (optional) PINS this call to a specific project instead of the lazily-read module
// var (`_activeProjectId`). During a multi-file upload the user can switch projects mid-batch;
// the interceptor would then stamp in-flight files with the NEW project and the server would
// import them into the wrong one (a real cross-project misroute, not just a mislabel). The upload
// pipeline captures the project ONCE when the batch starts and threads it here so every file
// lands where it was dropped. Omitted → unchanged behavior (interceptor supplies the header).
export async function getPresignedUrl(fileName, fileType, fileHash, fileSize = 0, fileCategory = "general_data", projectId = null) {
  const headers = await getAuthHeaders();
  if (projectId) headers["X-Project-Id"] = projectId;
  const response = await axios.post(
    `${API_BASE}/upload/presigned-url`,
    { file_name: fileName, file_type: fileType, file_hash: fileHash, file_size: fileSize, file_category: fileCategory },
    { headers }
  );
  return response.data; // { upload_url, raw_upload_url, s3_key, raw_s3_key, log_id }
}

/**
 * Classify a file based on its CSV headers using AI.
 * Returns the detected category, column mapping, and fill defaults.
 */
// `projectId` (optional) pins classification to the batch's captured project — same reason as
// getPresignedUrl. Classification reads the project's schema catalog (known categories/mappings),
// so a mid-batch switch must not make a file classify against a different project's schemas.
export async function classifyFile(headers, fileName, sampleRows = null, projectId = null) {
  const authHeaders = await getAuthHeaders();
  if (projectId) authHeaders["X-Project-Id"] = projectId;
  const body = { headers, file_name: fileName };
  if (sampleRows) body.sample_rows = sampleRows;
  const response = await axios.post(
    `${API_BASE}/upload/classify`,
    body,
    { headers: authHeaders }
  );
  return response.data; // { category, confidence, description, column_mapping, fill_defaults, required_columns }
}

/**
 * Server-authoritative ingest (Option B, flag `server_side_ingest`).
 * After the browser uploads ONLY the raw file to the presigned raw URL, call this
 * with the presigned `log_id`. The server classifies, parses Excel (openpyxl — no
 * SheetJS date shift), cleans, per-project dedups, writes the clean file, and finalizes
 * the FileLog. Project scope is attached automatically via the axios interceptor.
 * Returns: { status, decision, category, s3_key, rows_imported, duplicates_removed,
 *            original_rows, report, message }.
 */
// `projectId` (optional) pins the ingest to the batch's captured project — the critical one: this
// is the call that writes the clean file and imports rows, so an unpinned ingest after a mid-batch
// switch imports the file's data into the wrong project. Omitted → interceptor supplies the header.
export async function ingestFile(logId, projectId = null) {
  const headers = await getAuthHeaders();
  if (projectId) headers["X-Project-Id"] = projectId;
  const response = await axios.post(
    `${API_BASE}/upload/ingest`,
    { log_id: logId },
    { headers }
  );
  return response.data;
}

/**
 * Upload file directly to S3 using the pre-signed URL.
 */
export async function uploadToS3(presignedUrl, file, onProgress) {
  await axios.put(presignedUrl, file, {
    headers: { "Content-Type": "text/csv" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
  });
}

/**
 * Fetch file processing history.
 */
export async function fetchFileHistory(fileType = null) {
  const headers = await getAuthHeaders();
  const params = fileType ? { file_type: fileType } : {};
  const response = await axios.get(`${API_BASE}/files/`, { headers, params });
  return response.data;
}

/**
 * Fetch status of a single file log entry.
 */
export async function fetchFileStatus(logId) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/files/${logId}`, { headers });
  return response.data;
}
